import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../redux';
import {
  toggleMeasureAsBar,
  toggleMeasureAsLine,
} from '../../../redux/ChartPoperties/ChartControlsActions';
import { Box, Typography, Tooltip } from '@mui/material';
import { checkMinRequiredCards } from '../../ChartAxes/chartDataFunctions/checkMinRequiredCards';

/**
 * Allows user to select which measures should be displayed as line charts instead of bar charts(for multi bar and horizontal bar chart) and vice versa for line and area chart.
 * @returns Bar/Line settings component.
 */
const BarLineSettings = ({ iconColor }: { iconColor?: string }) => {
  const { chartProperties, propKey, chartAxes, barLineSettings, chartType } = useSelector(
    (state: RootState) => {
      const chartProperties = state.chartProperties;
      const tabTileProps = state.tabTileProps;
      const propKey = `${tabTileProps.selectedTabId}.${tabTileProps.selectedTileId}`;
      const chartAxes = state.chartProperties.properties[propKey]?.chartAxes || [];
      const barLineSettings = state.chartControls.properties[propKey]?.barLineSettings;
      const chartType = state.chartProperties.properties[propKey]?.chartType;

      return { chartProperties, propKey, chartAxes, barLineSettings, chartType };
    },
  );

  const dispatch = useDispatch();

  const measureAxis = chartAxes.find((axis: any) => axis.name === 'Measure');
  const measures = measureAxis?.fields || [];

  const handleToggleMeasure = (measureUId: string) => {
    if (chartType === 'multibar' || chartType === 'horizontalBar') {
      let newLineMeasuresList = [...barLineSettings?.lineMeasures];
      if (barLineSettings?.lineMeasures.includes(measureUId)) {
        let measureIndex = barLineSettings?.lineMeasures.findIndex(
          (uId: string) => uId === measureUId,
        );
        newLineMeasuresList.splice(measureIndex, 1);
      } else {
        newLineMeasuresList.push(measureUId);
      }
      dispatch(toggleMeasureAsLine(propKey, newLineMeasuresList));
    }
    if (chartType === 'line' || chartType === 'area') {
      let newLineMeasuresList = [...barLineSettings?.barMeasures];
      if (barLineSettings?.barMeasures.includes(measureUId)) {
        let measureIndex = barLineSettings?.barMeasures.findIndex(
          (uId: string) => uId === measureUId,
        );
        newLineMeasuresList.splice(measureIndex, 1);
      } else {
        newLineMeasuresList.push(measureUId);
      }
      dispatch(toggleMeasureAsBar(propKey, newLineMeasuresList));
    }
  };

  if (!checkMinRequiredCards(chartProperties.properties[propKey], chartType)) {
    return (
      <Box sx={{ padding: '1rem' }}>
        <Typography variant="body2" sx={{ color: iconColor ? iconColor : 'textSecondary' }}>
          No measures available. Add measures and populate the visual to configure Bar/Line
          settings.
        </Typography>
      </Box>
    );
  }

  const isDisabled = (isLine: boolean, isBar: boolean) => {
    if (chartType === 'multibar' || chartType === 'horizontalBar') {
      if (
        isLine === false &&
        Math.abs(measures?.length - barLineSettings?.lineMeasures?.length) <= 1
      ) {
        return true;
      } else {
        return false;
      }
    }

    if (chartType === 'line' || chartType === 'area') {
      if (
        isBar === false &&
        Math.abs(measures?.length - barLineSettings?.barMeasures?.length) <= 1
      ) {
        return true;
      } else {
        return false;
      }
    }
  };

  /**
   *
   * @returns checkbox section
   */
  function MeasureWithCheckBoxes() {
    return (
      <div className="check-boxes" style={{ paddingLeft: '0.6rem' }}>
        {measures?.map((measure: any) => {
          const isLine = barLineSettings.lineMeasures?.includes(measure.uId) || false;
          const isBar = barLineSettings.barMeasures?.includes(measure.uId) || false;
          const label = measure.displayname || measure.fieldname;
          const disabled = isDisabled(isLine, isBar);

          const tooltipTitle = disabled
            ? chartType === 'multibar' || chartType === 'horizontalBar'
              ? 'At least one measure must remain as a bar chart'
              : 'At least one measure must remain as a line chart'
            : '';

          const uniqueId = `measure-check-${measure.uId}`;
          return (
            <Tooltip
              title={tooltipTitle}
              placement="bottom"
              slotProps={{
                tooltip: {
                  sx: {
                    fontSize: '12px',
                  },
                },
              }}
            >
              <div
                style={{
                  marginBottom: '1px',
                  fontSize: '13px',
                  display: 'flex',
                  gap: '0.4rem',
                  alignItems: 'center',
                }}
              >
                <input
                  // className="checkbox_tick"
                  type="checkbox"
                  id={uniqueId}
                  value={measure.uId}
                  checked={
                    chartType === 'multibar' || chartType === 'horizontalBar' ? isLine : isBar
                  }
                  disabled={disabled}
                  onChange={() => handleToggleMeasure(measure.uId)}
                  style={{
                    accentColor: 'rgb(150 146 146)',
                    verticalAlign: 'middle',
                    transform: 'scale(0.87)',
                    marginLeft: 0,
                    marginRight: 0,
                    pointerEvents: disabled ? 'none' : 'auto',
                    cursor: disabled ? 'none' : 'pointer',
                  }}
                />
                <label htmlFor={uniqueId} style={{ color: iconColor ? iconColor : '#404040' }}>
                  {label}
                </label>
              </div>
            </Tooltip>
          );
        })}
      </div>
    );
  }

  return (
    <>
      <Box
        className="optionDescription"
        style={{
          marginBottom: '5px',
          marginTop: '15px',
          justifyContent: 'space-between',
          paddingRight: '0.7rem',
        }}
      >
        <span style={{ textTransform: 'uppercase', color: iconColor ? iconColor : '#404040' }}>
          {chartType === 'line' || chartType === 'area' ? 'Convert to Bar' : 'Convert to Line'}
        </span>
        <Box
          style={{
            visibility:
              ((chartType === 'line' || chartType === 'area') &&
                barLineSettings?.barMeasures?.length > 0) ||
              ((chartType === 'multibar' || chartType === 'horizontalBar') &&
                barLineSettings?.lineMeasures?.length > 0)
                ? 'visible'
                : 'hidden',
          }}
        >
          <Box>
            <Tooltip
              title="Reset"
              slotProps={{
                tooltip: {
                  sx: {
                    fontSize: '12px',
                  },
                },
              }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="14"
                height="14"
                viewBox="0 0 24 24"
                color={iconColor || '#404040'}
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                className="lucide lucide-eraser-icon lucide-eraser"
                onClick={() => {
                  if (chartType === 'multibar' || chartType === 'horizontalBar') {
                    dispatch(toggleMeasureAsLine(propKey, []));
                  }
                  if (chartType === 'line' || chartType === 'area') {
                    dispatch(toggleMeasureAsBar(propKey, []));
                  }
                }}
              >
                <path d="M21 21H8a2 2 0 0 1-1.42-.587l-3.994-3.999a2 2 0 0 1 0-2.828l10-10a2 2 0 0 1 2.829 0l5.999 6a2 2 0 0 1 0 2.828L12.834 21" />
                <path d="m5.082 11.09 8.828 8.828" />
              </svg>
            </Tooltip>
          </Box>
        </Box>
      </Box>
      <Typography
        sx={{
          fontWeight: 600,
          fontSize: '12px',
          color: iconColor ? iconColor : '#515050',
          textAlign: 'left',
          marginLeft: '0.6rem',
          marginBottom: '0.35rem',
        }}
      >
        Available Measures:
      </Typography>
      <MeasureWithCheckBoxes />
    </>
  );
};

export default BarLineSettings;
